import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	createFolder,
	CreateFolderModel,
	EagerLoadedChildren,
	IFolderViewModel
} from "client/Api/Email/email";
import { ReactElement, useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { isNotNil } from "client/Misc/utils";

const validationConfiguration: IValidationConfiguration<CreateFolderModel> = createValidationConfiguration<CreateFolderModel>();
validationConfiguration.forScalar("folderName")
	.add(
		isNotNullNorUndefined().activateOn("formSubmit"),
		isNotEmpty().activateOn("formSubmit")
	);

export type EmailCreateFolderTarget = {
	kind: "folderId";
	folderId: string;
	mailbox: string | null;
} | {
	kind: "mailbox-root";
	mailbox: string;
};

export type EmailCreateFolderDialogProps = {
	target: EmailCreateFolderTarget;
	onCreateFolder(newFolder: IFolderViewModel<EagerLoadedChildren>): void;
	onClose(): void;
};

export const EmailCreateFolderDialog = (props: EmailCreateFolderDialogProps) => {
	const {
		target,
		onClose,
		onCreateFolder
	} = props;

	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [model, setModel] = useState<CreateFolderModel>(createDefaultSaveModel(target));
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<CreateFolderModel> =
		useFormHandler(
			model,
			setModel,
			validationConfiguration
		);

	const confirmHandler = async (): Promise<void> => {
		try {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);
			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}
			setIsSaving(true);
			const newFolder: IFolderViewModel<EagerLoadedChildren> = await createFolder(
				model,
				globalCancellation
			);

			onCreateFolder(newFolder);
			setIsSaving(false);
			onClose();
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsSaving(false);
		}
	};

	return (
		<Dialog
			trackingId="email-create-folder-dialog"
			hasBusyIndicator={isSaving}
		>
			<Dialog.Header
				title={localize("_CreateANewFolder")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={confirmHandler}>
					<FormItem
						label={localize("_FolderName")}
						isRequired
						hasNoMargin
					>
						<TextInput
							{...formHandler.for("folderName").attachToTextInput()}
							autoFocus="focus"
						/>
						<ValidationError {...formHandler.for("folderName").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={confirmHandler}
						text={localize("_Done")}
						trackingId="confirm"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

EmailCreateFolderDialog.displayName = "EmailCreateFolderDialog";

export type EmailCreateFolderDialogInitialData = Omit<EmailCreateFolderDialogProps, "onCreateFolder" | "onClose">;

export function useEmailCreateFolderDialog(): [
	element: ReactElement | null,
	open: (
		initialData: EmailCreateFolderDialogInitialData
	) => Promise<TryResult<IFolderViewModel<EagerLoadedChildren>>>
] {
	return useDataInputDialog<
		EmailCreateFolderDialogInitialData,
		IFolderViewModel<EagerLoadedChildren>
	>(
		(initialData, onClose, onSave) => (
			<EmailCreateFolderDialog
				{...initialData}
				onClose={onClose}
				onCreateFolder={onSave}
			/>
		)
	);
}

function createDefaultSaveModel(
	target: EmailCreateFolderTarget
): CreateFolderModel {
	switch (target.kind) {
		case "folderId":
			return {
				folderName: "",
				location: {
					smtpAddress: isNotNil(target.mailbox)
						? {
							isSet: true,
							value: target.mailbox
						}
						: { isSet: false },
					folderId: {
						isSet: true,
						value: target.folderId
					}
				}
			};
		case "mailbox-root":
			return {
				folderName: "",
				location: {
					smtpAddress: {
						isSet: true,
						value: target.mailbox
					},
					folderId: { isSet: false }
				}
			};
		default:
			never(target);
	}
}